import api from "./axios-interceptor";

/**
 * Web Push for the operator console: register the service worker, get the browser's permission,
 * hand its subscription to the server.
 *
 * <p>Why: the STOMP socket only reaches a tab that is awake. A tab the operator is not looking at is
 * throttled and eventually frozen, the socket dies, and nothing rang until F5. A push subscription
 * is the browser's own address for waking this device without the page being awake — it is the
 * only mechanism that makes "you never have to refresh to know a customer wrote" actually true.
 *
 * <p>Everything here is best-effort and never throws to the caller: an unsupported browser, a
 * declined permission, or a server without push configured must degrade to exactly the console we
 * had before, not to an error on a screen somebody is working in.
 */

// Not "/sw.js". Cloudflare caches *.js by extension for hours, and before this worker existed the
// SPA fallback had already been served — and cached — as text/html at exactly that URL, which makes
// register() fail with a MIME SecurityError. A path the edge has never seen sidesteps the poisoned
// entry, and public/serve.json marks it no-cache so it can never be poisoned again.
const SW_URL = "/npe-sw.js";

/** OS-level notifications from the page itself. Needs no push service — works wherever the tab is alive. */
export function isNotificationSupported() {
    return typeof window !== "undefined" && "Notification" in window;
}

/** Background delivery via the push service, on top of the above. */
export function isPushSupported() {
    return isNotificationSupported()
        && "serviceWorker" in navigator
        && "PushManager" in window;
}

/** "granted" | "denied" | "default" | "unsupported" */
export function getPermissionState() {
    if (!isNotificationSupported()) return "unsupported";
    return Notification.permission;
}

let registrationPromise = null;

/** Register the worker once; safe to call repeatedly. Resolves null when unsupported or failing. */
export function registerServiceWorker() {
    if (!isPushSupported()) return Promise.resolve(null);
    if (!registrationPromise) {
        registrationPromise = navigator.serviceWorker
            .register(SW_URL, {scope: "/"})
            .catch((e) => {
                console.warn("[push] service worker registration failed", e);
                registrationPromise = null;
                return null;
            });
    }
    return registrationPromise;
}

/** The VAPID key arrives base64url; pushManager.subscribe wants raw bytes. */
function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/**
 * Ask the server for its VAPID public key. Null when push is not configured there (404), which the
 * caller reads as "do not offer notifications" rather than as a failure.
 */
async function fetchPublicKey() {
    try {
        const res = await api.get("/api/v1/push/public-key");
        return res?.publicKey || null;
    } catch (e) {
        if (e?.response?.status !== 404) console.warn("[push] could not fetch public key", e?.message);
        return null;
    }
}

/**
 * Turn on notifications for this browser: the OS-level permission first, then — if it is
 * available here — background delivery through the push service.
 *
 * <p>Two layers, deliberately separate. The permission alone already lets the console show an OS
 * notification with the system sound whenever a message arrives while the tab is open but not
 * focused — that path is the console's own and needs no third party. Push adds the frozen/closed
 * tab case, but it rides Google's or Mozilla's push service, which some networks block; a failure
 * there must not take the first layer down with it, which is why permission is requested BEFORE any
 * of the push plumbing and why a push failure is reported as "notifications-only", not "failed".
 *
 * <p>Must be called from a user gesture the first time (a click), because browsers only show the
 * permission prompt in response to one; called later with permission already granted it works
 * silently, which is how a returning operator is re-registered on every login without being asked.
 *
 * @returns {Promise<"subscribed"|"notifications-only"|"denied"|"unsupported"|"failed">}
 *   subscribed         – permission granted AND the push subscription is registered with the server
 *   notifications-only – permission granted; push unavailable here (blocked, unsupported, or the
 *                        server has no push configured). OS notifications still work while the tab lives.
 *   denied             – the browser said no
 */
export async function enablePushNotifications() {
    if (!isNotificationSupported()) return "unsupported";

    let permission;
    try {
        permission = Notification.permission;
        if (permission === "default") {
            permission = await Notification.requestPermission();
        }
    } catch (e) {
        console.warn("[push] permission request failed", e);
        return "failed";
    }
    if (permission !== "granted") return "denied";

    if (!isPushSupported()) return "notifications-only";

    try {
        const registration = await registerServiceWorker();
        if (!registration) return "notifications-only";

        const publicKey = await fetchPublicKey();
        if (!publicKey) return "notifications-only";

        // Reuse an existing subscription if the browser already has one for this key; a fresh
        // subscribe every login would hand the server a new endpoint each time and orphan the old.
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
        }

        await api.post("/api/v1/push/subscribe", subscription.toJSON());
        return "subscribed";
    } catch (e) {
        // Typically AbortError "push service error": the browser could not reach FCM/Mozilla.
        console.warn("[push] background push unavailable here; OS notifications still on", e?.message || e);
        return "notifications-only";
    }
}

/**
 * Show an OS-level notification from the page itself — the console's own alert, independent of
 * any push service. Used when a message arrives over STOMP while the tab is alive but the operator
 * is not looking at it (another window, another tab, minimised). The system plays its own
 * notification sound as well, on top of the console's chime.
 *
 * <p>Goes through the service worker registration when there is one, so a tap is handled by the
 * same `notificationclick` code as a push (focus the console, open the chat) and so the same `tag`
 * collapses it with a push for the same chat instead of showing two. Falls back to a plain
 * `Notification` where there is no worker.
 *
 * @param {{title: string, body?: string, url?: string, tag?: string, onClick?: () => void}} n
 */
export async function showLocalNotification(n) {
    if (getPermissionState() !== "granted") return false;
    const title = n.title || "پیام جدید";
    const options = {
        body: n.body || "",
        icon: "/logo.png",
        badge: "/logo.png",
        dir: "rtl",
        lang: "fa",
        tag: n.tag || "npe-chat",
        renotify: true,
        requireInteraction: true,
        silent: false,
        data: {url: n.url || "/dashboard/chats"},
    };
    try {
        if ("serviceWorker" in navigator) {
            const reg = await navigator.serviceWorker.getRegistration(SW_URL);
            if (reg && reg.active) {
                await reg.showNotification(title, options);
                return true;
            }
        }
    } catch (e) {
        /* fall through to the plain constructor */
    }
    try {
        const note = new Notification(title, options);
        note.onclick = () => {
            try { window.focus(); } catch (e) { /* ignore */ }
            if (typeof n.onClick === "function") n.onClick();
            note.close();
        };
        return true;
    } catch (e) {
        console.warn("[push] could not show local notification", e);
        return false;
    }
}

/** Forget this browser on the server and in the push service. Best-effort. */
export async function disablePushNotifications() {
    if (!isPushSupported()) return;
    try {
        const registration = await navigator.serviceWorker.getRegistration(SW_URL);
        const subscription = await registration?.pushManager.getSubscription();
        if (!subscription) return;
        try {
            await api.post("/api/v1/push/unsubscribe", {endpoint: subscription.endpoint});
        } catch {
            /* the server row is pruned on its next failed send anyway */
        }
        await subscription.unsubscribe();
    } catch (e) {
        console.warn("[push] disabling notifications failed", e);
    }
}
